import { cx } from 'linaria';
import { styled } from 'linaria/react';
import React, { useState, useContext } from 'react';
import { theme } from '../Theme';
import ChannelContext from '@jetshop/core/components/ChannelContext';
import { Popup } from './Popup';

export const SectionHeading = styled('h2')`
  font-weight: 600;
  font-size: 15px;
  color: #bdbdbd;
  line-height: 18px;
  margin-bottom: 0.25em;

  &.active {
    color: ${theme.colors.primary};
    text-decoration: underline;
  }
`;

export const ProductSection = styled('section')``;

const ProductInfoSection = styled('div')`
  display: flex;
  width: 100%;
  padding: 0 80px;
  &.has-thumbnails {
    padding: 0;
  }
`;

const ProductInfoWrapper = styled('div')`
  width: 100%;
  ${theme.below.lg} {
    margin: 0 20px;
  }

  .inactive-content {
    display: none;
  }
`;

const ProductInfoNav = styled('div')`
  display: flex;
  justify-content: flex-start;
  padding-bottom: 12px;

  button {
    font-weight: 400;
    font-size: 16px;
    text-transform: uppercase;
    line-height: 20px;
    letter-spacing: 0.02em;
    color: ${theme.colors.blackish};
    background: none;
    margin-right: 20px;

    &.active {
      font-weight: 700;
      border-bottom: 4px solid ${theme.colors.secondary};
      margin-bottom: -14px;
      padding-bottom: 10px;
    }
  }
  border-bottom: 2px solid ${theme.colors.border};

  &.Sandnas {
    button {
      &.active {
        border-bottom: 4px solid #e0b771;
      }
    }
  }
  &.Onlinecykel {
    button {
      &.active {
        border-bottom: 4px solid #ff0000;
      }
    }
  }
  &.XLCykel {
    button {
      &.active {
        border-bottom: 4px solid #fd0;
      }
    }
  }
`;

const Description = styled('article')`
  margin-bottom: 42px;
  margin-top: 20px;
  color: #000;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.2px;

  td {
    line-height: 20px;
  }

  th {
    display: none;
  }

  span {
    font-size: 18px;
    line-height: 30px;
    font-weight: 700;
    text-decoration: none !important;
  }

  .specification-list__value {
    strong {
      padding-bottom: 20px;
    }
  }

  h1,
  h2,
  h3 {
    margin: 10px 0 0px 0;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.2px;
  }

  li {
    list-style-type: disc;
    list-style-position: inside;
  }
`;

const ListGroupWrapper = styled('div')`
  width: 100%;
  display: inline-block;
  margin-top: 20px;

  li {
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-top: 8px;
    border-bottom: 1px solid ${theme.colors.border};
    text-align: right;
  }

  .title {
    min-width: fit-content;
  }

  ${theme.below.lg} {
    li {
      text-align: right;
      font-size: 14px;
    }
  }
`;

const InfoButton = styled('button')`
  margin-right: 4px;
  margin-left: 2px;
  background: ${theme.colors.blackish};
  width: 12px;
  height: 12px;
  color: white;
  font-size: 8px;
  font-weight: 700;
  border-radius: 50%;
  vertical-align: text-top;
  text-align: center;
`;

const ListItem = ({ item, tip }) => {
  const itemValue = item => {
    switch (item?.type) {
      case 'STRING':
        return item?.stringValue;
      case 'BOOL':
        return item.boolValue ? 'Ja' : 'Nej';
      default:
        return null;
    }
  };
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      {item && (
        <li className="list-item">
          <span
            style={{ marginRight: '10px', fontWeight: '600' }}
            className="title"
          >
            {item?.title}
            {tip && (
              <>
                <InfoButton
                  onClick={() => {
                    setIsOpen(true);
                  }}
                >
                  i
                </InfoButton>
                <Popup isOpen={isOpen} setIsOpen={setIsOpen} content={tip} />
              </>
            )}
            :
          </span>
          <span>{itemValue(item)}</span>
        </li>
      )}
    </>
  );
};

const ListGroup = ({ productSpecification }) => {
  const emptyGroups = productSpecification?.items[0]?.filter(
    item => item === undefined
  );

  if (emptyGroups?.length === productSpecification?.items[0]?.length) {
    return null;
  }

  return (
    <ListGroupWrapper>
      <h3>{productSpecification?.title}</h3>
      <ul>
        {productSpecification?.items[0]?.map(item => {
          return <ListItem item={item} tip={item?.tip} />;
        })}
      </ul>
    </ListGroupWrapper>
  );
};

const TabContentWrapper = styled('div')`
  column-count: 2;
  grid-gap: 40px;

  .list-item {
    display: flex;
  }

  ${theme.below.lg} {
    column-count: 1;
  }
`;

const TabsContent = ({ productSpecifications }) => {
  return (
    <TabContentWrapper>
      {productSpecifications?.map(productSpecification => {
        return <ListGroup productSpecification={productSpecification} />;
      })}
    </TabContentWrapper>
  );
};

const ProductInfo = ({ product, productSpecifications }) => {
  const hasMultipleImages = product?.images?.length > 0;
  const { selectedChannel } = useContext(ChannelContext);

  const tabs = [
    {
      name: 'Produktsbeskrivning',
      content: product.description && (
        <div>
          <Description
            dangerouslySetInnerHTML={{
              __html: product.description
            }}
          />
        </div>
      )
    },
    {
      name: 'Specifikationer',
      content: productSpecifications && (
        <div>
          <TabsContent productSpecifications={productSpecifications} />
        </div>
      )
    }
  ];

  const [activeSection, setActiveSection] = useState(tabs[0].name);

  return (
    <>
      <ProductInfoSection className={hasMultipleImages && 'has-thumbnails'}>
        <ProductInfoWrapper>
          <ProductInfoNav className={cx(selectedChannel?.name)}>
            {tabs.map((tab, index) => (
              <button
                key={tab?.name + index}
                onClick={() => setActiveSection(tab.name)}
                className={cx(
                  activeSection === tab.name ? 'active' : 'inactive'
                )}
              >
                {tab.name}
              </button>
            ))}
          </ProductInfoNav>
          {tabs.map(tab => (
            <div
              className={cx(
                tab.name === activeSection
                  ? 'active-content'
                  : 'inactive-content'
              )}
            >
              <React.Fragment key={tab.name}>{tab.content}</React.Fragment>
            </div>
          ))}
        </ProductInfoWrapper>
      </ProductInfoSection>
    </>
  );
};

export default ProductInfo;
